import {Injectable} from '@angular/core';
import {HttpClientService} from '../http-client/http-client.service';
import {EnvironmentService} from '../environment/environment.service';
import {combineLatest, Observable, of, OperatorFunction, pipe, shareReplay, switchMap} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {HttpParams, HttpResponse} from '@angular/common/http';
import {Delivery} from '../../model/delivery';
import {DeliveryDescription} from '../../model/delivery-description';
import {DeliveryProduct} from '../../model/delivery-product';
import {DeliveryProductDto} from './delivery-product-dto';
import {DeliveryDescriptionDto} from './delivery-description-dto';
import {DeliveryDescriptionResponseDto} from './delivery-description-response-dto';
import {DeliveryProductWithDeliveryDescription} from '../../model/delivery-product-with-delivery-description';
import {DeliveryType} from '../../model/delivery-type';
import {DeliveryTypeDto} from './delivery-type-dto';
import {DeliveryProductResponseDto} from './delivery-product-response-dto';
import {PersonalService} from '../personal/personal.service';
import {TranslateService} from '@ngx-translate/core';
import {DeliveryDescriptionsByDateCategory} from '../../model/deliveryDescriptionsByDateCategory';
import {DeliveryProductType} from '../../model/delivery-product-type';
import {DeliveryProductTypeDto} from './delivery-product-type-dto';
import {TypeGuards} from '../../utils/TypeGuards';


@Injectable({
  providedIn: 'root'
})
export class DeliveryService {

  constructor(private readonly httpClientService: HttpClientService,
              private readonly environmentService: EnvironmentService,
              private readonly translate: TranslateService,
              private readonly personalService: PersonalService) {
  }

  private readonly hknrKanzlei = 88140;
  private readonly hknrLfu = 88960;
  private readonly hknrMittelstand = 88991;
  private readonly hknrMittelstandMitRechnungswesen = 88980;

  private latestDelivery?: [string, Observable<Delivery>];
  private latestDeliveryDescriptions$?: Observable<DeliveryDescription[]>;

  private lastDeliveryStateIsPersonalized = false;
  private lastDeliveryDescriptionsStateIsPersonalized = false;

  deliveriesResource = this.environmentService.beUrl + 'deliveries';

  deliveryFilterTemplateSeparator = ',';
  deliveryFilterAssociatedTemplate = 'filter-associated';

  static validateDateIsInFuture(deliveryDate: Date): boolean {
    const currentDate = new Date();
    const comparisonDate = new Date(deliveryDate);
    return comparisonDate >= currentDate;
  }

  deliveryDescriptionByIdResourceTemplate = (deliveryId: string) : string => `${this.deliveriesResource}/${deliveryId}/info`;
  deliveryProductsByIdResourceTemplate = (deliveryId: string) : string => `${this.deliveriesResource}/${deliveryId}/products`;

  deliveryDescriptionsResourceTemplate = () : string => `${this.deliveriesResource}/info`;
  deliveryProductsRouteTemplate = (deliverId: string ) : string => `/delivery/products/${deliverId}`;
  deliveryMainReleaseProductsRouteTemplate = (deliveryId: string) : string => `/delivery/main-products/${deliveryId}`;

  getDeliveryProductsRoute(deliveryType: DeliveryType, deliveryId: string): string {
    if (deliveryType === DeliveryType.ServiceRelease || deliveryType === DeliveryType.Hotfix || deliveryType === DeliveryType.ServiceReleaseAndHotfix) {
      return this.deliveryProductsRouteTemplate(deliveryId);
    }
    if (deliveryType === DeliveryType.MainRelease) {
      return this.deliveryMainReleaseProductsRouteTemplate(deliveryId);
    }
    return '';
  }

  getDeliveryById(deliveryId: string, isPersonalized: boolean, adjustLargeLfUName: boolean = false): Observable<Delivery> {

    if (!this.latestDelivery || this.latestDelivery[0] !== deliveryId || this.lastDeliveryStateIsPersonalized !== isPersonalized) {

      const deliveryDescriptionObservable = this.getDeliveryDescriptionById(deliveryId, adjustLargeLfUName);
      const deliveryProductsObservable = this.getDeliveryProductsById(deliveryId, isPersonalized);

      const deliveryObservable: Observable<Delivery> = combineLatest([deliveryDescriptionObservable, deliveryProductsObservable],
        (varDescription: DeliveryDescription, varProducts: DeliveryProduct[]) => {
          this.lastDeliveryStateIsPersonalized = isPersonalized;
          return {description: varDescription, products: varProducts};
      }).pipe(
        catchError(err => {
          // Reset cache
          this.latestDelivery = ['', of()];
          throw err;
        })
      );

      this.latestDelivery = [deliveryId, deliveryObservable];
    }
    return this.latestDelivery[1];
  }

  getDeliveryDescriptionById(deliveryId: string, adjustLargeLfUName: boolean = false): Observable<DeliveryDescription> {
      return this.httpClientService
        .get<DeliveryDescriptionDto>(this.deliveryDescriptionByIdResourceTemplate(deliveryId))
        .pipe(
          map((resp: HttpResponse<DeliveryDescriptionDto>) => this.mapDeliveryDescription(resp.body)),
          map(deliveryDescription => {
            if (adjustLargeLfUName) {
              deliveryDescription = this.adjustLargeLfUName([deliveryDescription])[0];
            }
            return deliveryDescription;
          }),
          shareReplay(1));
  }

  getDeliveryProductsById(deliveryId: string, isPersonalized: boolean): Observable<DeliveryProduct[]> {

    if (isPersonalized) {
      return this.personalService.activeBusinessPartner$.pipe(
        switchMap(businessPartner => this.personalService.getBusinessPartnerFilterHKNRs(businessPartner.businessPartnerId)),
        switchMap(filterHKNRs => {

          let httpParams = new HttpParams();
          httpParams = httpParams.append(this.deliveryFilterAssociatedTemplate,
            filterHKNRs.map(item => item).join(this.deliveryFilterTemplateSeparator));

          return this.httpClientService.get<DeliveryProductResponseDto>(`${this.deliveryProductsByIdResourceTemplate(deliveryId)}?`
            + httpParams.toString())
            .pipe(
              map((resp: HttpResponse<DeliveryProductResponseDto>) => this.mapDeliveryProducts(resp.body))
            );
        })
      );
    } else {
      return this.httpClientService
      .get<DeliveryProductResponseDto>(this.deliveryProductsByIdResourceTemplate(deliveryId))
        .pipe(
          map((resp: HttpResponse<DeliveryProductResponseDto>) => this.mapDeliveryProducts(resp.body)),
          shareReplay(1));
    }
  }

  getDeliveryProductWithDeliveryDescriptionById(deliveryId: string, deliveryProductId: string):
    Observable<DeliveryProductWithDeliveryDescription> {
    return this.getDeliveryById(deliveryId, false).pipe(
      map((delivery: Delivery) => this.mapDeliveryProductDetailsData(delivery, deliveryProductId))
    );
  }

  getDeliveryDescriptions(isPersonalized: boolean): Observable<DeliveryDescription[]> {
    if (!this.latestDeliveryDescriptions$ || this.lastDeliveryDescriptionsStateIsPersonalized !== isPersonalized) {
      this.lastDeliveryDescriptionsStateIsPersonalized = isPersonalized;

      if (isPersonalized) {
        this.latestDeliveryDescriptions$ =  this.personalService.activeBusinessPartner$.pipe(
          switchMap(businessPartner => this.personalService.getBusinessPartnerFilterHKNRs(businessPartner.businessPartnerId)),
          switchMap(filterHKNRs => {

            let httpParams = new HttpParams();
            httpParams = httpParams.append(this.deliveryFilterAssociatedTemplate,
              filterHKNRs.map(item => item).join(this.deliveryFilterTemplateSeparator));

            return this.httpClientService.get<DeliveryDescriptionResponseDto>(
              `${this.deliveryDescriptionsResourceTemplate()}?`+ httpParams.toString());
          }),
          this.transformDeliveryDescriptions(),
          shareReplay(1)
        );
      } else {
        this.latestDeliveryDescriptions$ = this.httpClientService
          .get<DeliveryDescriptionResponseDto>(this.deliveryDescriptionsResourceTemplate())
          .pipe(
            this.transformDeliveryDescriptions(),
            shareReplay(1)
          );
      }
    }
    return this.latestDeliveryDescriptions$;
  }

  private transformDeliveryDescriptions(): OperatorFunction<HttpResponse<DeliveryDescriptionResponseDto>, DeliveryDescription[]> {
    return pipe(
      map((resp: HttpResponse<DeliveryDescriptionResponseDto>) => this.mapDeliveryDescriptionResponse(resp.body)),
      map(deliveryDescriptions => this.filterSmallLfUDeliveryDescriptions(deliveryDescriptions)),
      map(deliveryDescriptions => this.filterMittelstandMitRechnungswesenDeliveryDescriptions(deliveryDescriptions)),
      map(deliveryDescriptions => this.adjustLargeLfUName(deliveryDescriptions)),
      map(deliveryDescriptions => this.mapDeliveryDescriptionLinks(deliveryDescriptions)),
    );
  }

  getDeliveryDescriptionsByDateCategory(isPersonalized: boolean): Observable<DeliveryDescriptionsByDateCategory> {

    return this.getDeliveryDescriptions(isPersonalized).pipe(
      map((descriptions: DeliveryDescription[]) => {
        // Nur gültige Deliveries mit ID und DeliveryType
        const validDeliveries = descriptions.filter(d => !!d.id && d.deliveryType !== null);

        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Hilfsfunktion: Normalisiere Datum auf 00:00 Uhr (ignoriere Uhrzeit)
        const normalizeDate = (date: Date): Date => {
          return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        };

        // Hilfsfunktion: Prüfe ob Delivery ein ServiceRelease ist (SR oder SRAH)
        const isServiceRelease = (deliveryType: DeliveryType): boolean => {
          return deliveryType === DeliveryType.ServiceRelease ||
            deliveryType === DeliveryType.ServiceReleaseAndHotfix;
        };

        // Sortierung: Absteigend nach Datum, dann nach DeliveryType, dann nach Name
        const sortDeliveryDescriptions = (a: DeliveryDescription, b: DeliveryDescription): number => {
          const dateA = normalizeDate(a.deliveryDate).getTime();
          const dateB = normalizeDate(b.deliveryDate).getTime();

          if (dateA !== dateB) {
            return dateB - dateA; // Absteigend nach Datum
          }

          if (a.deliveryType !== b.deliveryType) {
            return a.deliveryType - b.deliveryType; // Bei gleichem Datum nach DeliveryType
          }

          const nameA = a.deliveryName ?? '';
          const nameB = b.deliveryName ?? '';
          // 'base' sensitivity ignoriert Groß-/Kleinschreibung, aber nicht Akzente
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        };

        // SR_last: Letztes ServiceRelease bis einschließlich heute
        const pastServiceReleases = validDeliveries
          .filter(d => {
            const normalizedDate = normalizeDate(d.deliveryDate);
            return isServiceRelease(d.deliveryType) &&
              normalizedDate.getTime() <= currentDate.getTime();
          })
          .sort(sortDeliveryDescriptions);

        const lastServiceReleaseDate = pastServiceReleases.length > 0
          ? normalizeDate(pastServiceReleases[0].deliveryDate)
          : new Date(0);

        // SR_next: Erstes ServiceRelease ab morgen
        const futureServiceReleases = validDeliveries
          .filter(d => {
            const normalizedDate = normalizeDate(d.deliveryDate);
            return isServiceRelease(d.deliveryType) &&
              normalizedDate.getTime() > currentDate.getTime();
          })
          .sort((a, b) => {
            // Aufsteigend sortieren, um das zeitlich erste zu finden
            const dateA = normalizeDate(a.deliveryDate).getTime();
            const dateB = normalizeDate(b.deliveryDate).getTime();
            return dateA - dateB;
          });

        // Fallback: Wenn kein SR_next existiert, verwende heute + 14 Tage
        const nextServiceReleaseDate = futureServiceReleases.length > 0
          ? normalizeDate(futureServiceReleases[0].deliveryDate)
          : new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14);

        // Zukünftig: Nach SR_next (exklusive SR_next)
        const futureDeliveries = validDeliveries
          .filter(d => {
            const normalizedDate = normalizeDate(d.deliveryDate);
            return normalizedDate.getTime() > nextServiceReleaseDate.getTime();
          })
          .sort(sortDeliveryDescriptions);

        // Aktuell: Von SR_last bis SR_next (beide inklusive)
        const currentDeliveries = validDeliveries
          .filter(d => {
            const normalizedDate = normalizeDate(d.deliveryDate);
            return normalizedDate.getTime() >= lastServiceReleaseDate.getTime() &&
              normalizedDate.getTime() <= nextServiceReleaseDate.getTime();
          })
          .sort(sortDeliveryDescriptions);

        // Vergangen: Vor SR_last (exklusive SR_last), nur mit Produkten
        const pastDeliveries = validDeliveries
          .filter(d => {
            const normalizedDate = normalizeDate(d.deliveryDate);
            return normalizedDate.getTime() < lastServiceReleaseDate.getTime() &&
              d.totalProducts > 0;
          })
          .sort(sortDeliveryDescriptions);

        return {
          categories: [
            {
              categoryName: this.translate.instant('delivery-all-items.future_delivery_panel_title'),
              deliveries: futureDeliveries
            },
            {
              categoryName: this.translate.instant('delivery-all-items.current_delivery_panel_title'),
              deliveries: currentDeliveries
            },
            {
              categoryName: this.translate.instant('delivery-all-items.past_delivery_panel_title'),
              deliveries: pastDeliveries
            }
          ]
        };
      })
    );
  }

  private mapDeliveryProducts(deliveryProductResponseDto: DeliveryProductResponseDto | null): DeliveryProduct[] {

    if (null === deliveryProductResponseDto || null === deliveryProductResponseDto.delivery_products) {
      return new Object(null) as DeliveryProduct[];
    }
    const deliveryProducts: DeliveryProduct[] = [];

    if (null != deliveryProductResponseDto.delivery_products) {
      deliveryProductResponseDto.delivery_products.forEach(productDto => {
        deliveryProducts.push(this.mapDeliveryProduct(productDto));
      });
    }
    return deliveryProducts;
  }

  private mapDeliveryDescription(deliveryDescriptionDto: DeliveryDescriptionDto | null): DeliveryDescription {
    if (null === deliveryDescriptionDto) {
      return new Object(null) as DeliveryDescription;
    }
    const deliveryType = this.mapDeliveryType(deliveryDescriptionDto);
    if (null === deliveryType) {
      return new Object(null) as DeliveryDescription;
    }
    return {
      id: deliveryDescriptionDto.id,
      // delivery_name ist optional
      deliveryName: this.mapDeliveryName(deliveryDescriptionDto),
      deliveryDate: new Date(deliveryDescriptionDto.delivery_date),
      deliveryType,
      totalProducts: deliveryDescriptionDto.total_products,
      fullVersionCount: deliveryDescriptionDto.full_version_count,
      serviceReleaseCount: deliveryDescriptionDto.service_release_count,
      hotfixCount: deliveryDescriptionDto.hotfix_count,
      hknr: deliveryDescriptionDto.hknr,
      routerLink: ''
    };
  }

  private mapDeliveryDescriptionLinks(deliveryDescriptions: DeliveryDescription[]): DeliveryDescription[] {
    return deliveryDescriptions.map(deliveryDescription => {
      if (deliveryDescription?.deliveryType >= 0 && !!deliveryDescription?.id) {
        deliveryDescription.routerLink = this.getRoute(deliveryDescription.deliveryType, deliveryDescription.id);
      }
      return deliveryDescription;
    });
  }

  private getRoute(deliveryType: DeliveryType, id: string): string | null {
    let route: string | null = null;

    if (!!id) {
      switch (deliveryType) {
        case DeliveryType.MainRelease:
          route = this.deliveryMainReleaseProductsRouteTemplate(id);
          break;
        case DeliveryType.CompactRelease:
          route = null;
          break;
        default:
          route = this.deliveryProductsRouteTemplate(id);
          break;
      }
    }

    return route;
  }

  private mapDeliveryType(deliveryDescriptionDto: DeliveryDescriptionDto): DeliveryType | null {
    switch (deliveryDescriptionDto.delivery_type) {
      case DeliveryTypeDto.MainRelease:
        if (deliveryDescriptionDto.hknr === this.hknrKanzlei || deliveryDescriptionDto.hknr === this.hknrLfu) {
          return DeliveryType.MainRelease;
        } else if (deliveryDescriptionDto.hknr === this.hknrMittelstand || deliveryDescriptionDto.hknr === this.hknrMittelstandMitRechnungswesen) {
          return DeliveryType.CompactRelease;
        } else {
          return null;
        }
      case DeliveryTypeDto.ServiceRelease:
        if(deliveryDescriptionDto.hotfix_count &&
          (deliveryDescriptionDto.service_release_count || deliveryDescriptionDto.full_version_count)) {
          return DeliveryType.ServiceReleaseAndHotfix;
        }
        if(deliveryDescriptionDto.hotfix_count) {
          return DeliveryType.Hotfix;
        }
        return DeliveryType.ServiceRelease;
      case DeliveryTypeDto.Hotfix:
        return DeliveryType.Hotfix;
      default:
        return null;
    }
  }

  private formatProductVersion(version: string | null | undefined): string | null {
    if (!version) {
      return null;
    }
    const trimmedVersion = version.trim();

    // Regex Prüfung, ob die Version dem erwarteten Format entspricht:
    // ^(\d{1,3})   : Gruppe 1: 1-3 Ziffern am Anfang
    // \.           : Ein Punkt
    // (\d{1,2})   : Gruppe 2: 1-2 Ziffern nach dem Punkt
    // ([A-Za-z])?  : Gruppe 3: Optional ein einzelner Buchstabe am Ende
    // $            : Ende des Strings
    const match = trimmedVersion.match(/^(\d{1,3})\.(\d{1,2})([a-zA-Z])?$/);
    if (!match) {
      return null;
    }

    // Erste Nummer: nur bei einstelligen Werten eine führende Null hinzufügen
    const major = match[1].length === 1 ? match[1].padStart(2, '0') : match[1];

    // Zweite Nummer zweistellig mit nachfolgenden Nullen
    const minor = match[2].padEnd(2, '0');

    // Buchstabe oder '0' wenn keiner vorhanden
    const suffix = match[3] || '0';

    return `${major}${minor}${suffix}`;
  }

  private mapDeliveryProduct(deliveryProductDto: DeliveryProductDto | null): DeliveryProduct {
    if (null === deliveryProductDto ||
        !TypeGuards.isNonEmptyString(deliveryProductDto.id) ||
        !TypeGuards.isNonEmptyString(deliveryProductDto.title)
    ) {
      return new Object(null) as DeliveryProduct;
    }

    let title = deliveryProductDto.title;

    if (deliveryProductDto.product_version_type === DeliveryProductTypeDto.Hotfix &&
      deliveryProductDto.product_key &&
      deliveryProductDto.product_version) {

      const formattedVersion = this.formatProductVersion(deliveryProductDto.product_version);

      if (formattedVersion) {
        title = `${deliveryProductDto.product_key}-${formattedVersion} (${deliveryProductDto.title})`;
      }
      // Falls formattedVersion null ist (Format ungültig), bleibt der Standard-Titel bestehen (bereits oben definiert)
    }
    // In allen anderen Fällen (kein Hotfix, kein Key, keine Version) bleibt der Standard-Titel bestehen.

    return {
      id: deliveryProductDto.id,
      title: title,
      // product_group ist optional
      productGroup: deliveryProductDto.product_group ?? null,
      productType: this.mapDeliveryProductType(deliveryProductDto.product_version_type),
      documentId: deliveryProductDto.document_id ?? '',
      documentUrl: deliveryProductDto.document_url ?? '',
      bootRequired: deliveryProductDto.boot_required ?? false,
      dataTransformation: deliveryProductDto.data_transformation ?? false,
      deliveryBases: deliveryProductDto.delivery_bases ?? [],
      changeCategories: deliveryProductDto.change_categories ?? [],
      deliveredByRdt: deliveryProductDto.delivered_by_rdt ?? true,
      deliveredByDownload: deliveryProductDto.delivered_by_download ?? false
    };
  }

  private mapDeliveryProductDetailsData(delivery: Delivery, deliveryProductId: string): DeliveryProductWithDeliveryDescription {
    const deliveryProduct = delivery.products.find((product: DeliveryProduct) => product.id === deliveryProductId);
    if (!!deliveryProduct) {
      return {
        deliveryDescription: delivery.description,
        product: deliveryProduct
      };
    }
    return new Object(null) as DeliveryProductWithDeliveryDescription;
  }

  private mapDeliveryDescriptionResponse(
    deliveryDescriptionResponseDto: DeliveryDescriptionResponseDto | null): DeliveryDescription[] {
    if (null === deliveryDescriptionResponseDto || null === deliveryDescriptionResponseDto.delivery_descriptions) {
      return new Object(null) as DeliveryDescription[];
    }

    return deliveryDescriptionResponseDto.delivery_descriptions
      .map(deliveryDescriptionDto => this.mapDeliveryDescription(deliveryDescriptionDto));
  }

  private filterSmallLfUDeliveryDescriptions(deliveryDescriptions: DeliveryDescription[]): DeliveryDescription[] {
    return deliveryDescriptions.filter(deliveryDescription => !this.isSmallLfU(deliveryDescription));
  }

  private adjustLargeLfUName(deliveryDescriptions: DeliveryDescription[]): DeliveryDescription[] {
    deliveryDescriptions.forEach(deliveryDescription => {
      if (this.isLargeLfU(deliveryDescription) && deliveryDescription.deliveryName && deliveryDescription.deliveryName.length > 1) {
        deliveryDescription.deliveryName = deliveryDescription.deliveryName?.substring(0, deliveryDescription.deliveryName?.length - 1);
      }
    });
    return deliveryDescriptions;
  }

  private isLargeLfU(deliveryDescription: DeliveryDescription): boolean {
    return deliveryDescription.hknr === this.hknrLfu && deliveryDescription.deliveryName?.endsWith('1');
  }

  private isSmallLfU(deliveryDescription: DeliveryDescription): boolean {
    return deliveryDescription.hknr === this.hknrLfu && deliveryDescription.deliveryName?.endsWith('0');
  }

  private filterMittelstandMitRechnungswesenDeliveryDescriptions(deliveryDescriptions: DeliveryDescription[]): DeliveryDescription[] {
    return deliveryDescriptions.filter(deliveryDescription => !this.isMittelstandMitRechnungswesen(deliveryDescription));
  }

  private isMittelstandMitRechnungswesen(deliveryDescription: DeliveryDescription): boolean {
    return deliveryDescription.hknr === this.hknrMittelstandMitRechnungswesen;
  }

  private mapDeliveryProductType(deliveryProductTypeDto: DeliveryProductTypeDto | null | undefined): DeliveryProductType | null {
    switch (deliveryProductTypeDto) {
      case DeliveryProductTypeDto.FullVersion:
        return DeliveryProductType.FullVersion;
      case DeliveryProductTypeDto.ServiceRelease:
        return DeliveryProductType.ServiceRelease;
      case DeliveryProductTypeDto.Hotfix:
        return DeliveryProductType.Hotfix;
      default:
        return null;
    }
  }

  private mapDeliveryName(deliveryDescriptionDto: DeliveryDescriptionDto): string {
    if(deliveryDescriptionDto.delivery_name) {
      return deliveryDescriptionDto.delivery_name;
    }

    const deliveryType = this.mapDeliveryType(deliveryDescriptionDto);
    switch (deliveryType) {
      case DeliveryType.MainRelease:
        return this.translate.instant('delivery-type-pipe.main_release');
      case DeliveryType.ServiceRelease:
        if (deliveryDescriptionDto.total_products <= 0) {
          // es könnte sich auch um ein ServiceRelease handeln, welches auch Hotfixe enthält
          return this.translate.instant('delivery-type-pipe.default');
        }
        return this.translate.instant('delivery-type-pipe.service_release');
      case DeliveryType.Hotfix:
        return this.translate.instant('delivery-type-pipe.hotfix');
      case DeliveryType.ServiceReleaseAndHotfix:
        return this.translate.instant('delivery-type-pipe.service_release_and_hotfix');
      default:
        return this.translate.instant('delivery-type-pipe.default');
    }

  }
}
